import React, { useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  LogIn,
  LogOut,
  Plus,
  Inbox,
  PanelLeftClose,
  PanelLeft,
  Users,
  Lock,
  ScrollText,
  UserCog,
  Menu,
  X,
  Settings2,
} from 'lucide-react';

function ActionButton({ icon: Icon, label, onClick, variant = 'ghost', disabled = false, mobile = false }) {
  return (
    <button
      className={`btn btn-${variant} ${mobile ? 'mobile-dock-btn' : ''}`}
      onClick={onClick}
      disabled={disabled}
      title={label}
      type="button"
    >
      <Icon size={mobile ? 18 : 15} />
      {!mobile && <span>{label}</span>}
      {mobile && <span className="mobile-dock-label">{label}</span>}
    </button>
  );
}

export default function Header({
  sidebarOpen,
  onToggleSidebar,
  onLogin,
  onOpenRequest,
  onCreateTask,
  onShowRequests,
  onManageUsers,
  onShowLogs,
  onChangePassword,
  onEditProfile,
  onShowSettingData,
  isMobile,
}) {
  const {
    user,
    logout,
    isAdmin,
    isManager,
    canCreateTask,
    canViewUsers,
    canViewLogs,
    canEditProfile,
  } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const requestAction = useMemo(() => {
    if (isAdmin || isManager) return onShowRequests;
    return onOpenRequest;
  }, [isAdmin, isManager, onOpenRequest, onShowRequests]);

  const requestLabel = isAdmin || isManager ? 'Yêu cầu' : 'Gửi yêu cầu';

  const menuItems = [
    user && { key: 'password', label: 'Đổi mật khẩu', icon: Lock, onClick: onChangePassword },
    canEditProfile && { key: 'profile', label: 'Đổi thông tin Profile', icon: UserCog, onClick: onEditProfile },
    isAdmin && { key: 'settings', label: 'Setting Data', icon: Settings2, onClick: onShowSettingData },
    user
      ? { key: 'logout', label: 'Đăng xuất', icon: LogOut, onClick: logout }
      : { key: 'login', label: 'Đăng nhập', icon: LogIn, onClick: onLogin },
  ].filter(Boolean);

  const handleMenuItemClick = (handler) => {
    setMenuOpen(false);
    handler?.();
  };

  return (
    <>
      <header className="header desktop-only">
        <div className="header-left">
          <button className="btn-icon" onClick={onToggleSidebar} title={sidebarOpen ? 'Thu gọn sidebar' : 'Mở sidebar'} type="button">
            {sidebarOpen ? <PanelLeftClose size={18} /> : <PanelLeft size={18} />}
          </button>
          <div className="header-logo">
            <img src="/logo.png" alt="Logo" className="header-logo-image" />
            <span>Tien Le's Tasks</span>
          </div>
        </div>

        <div className="header-actions">
          <ActionButton icon={Inbox} label={requestLabel} onClick={requestAction} />
          {canViewUsers && <ActionButton icon={Users} label="User" onClick={onManageUsers} />}
          {canViewLogs && <ActionButton icon={ScrollText} label="Logs" onClick={onShowLogs} />}
          {isAdmin && <ActionButton icon={Settings2} label="Setting Data" onClick={onShowSettingData} />}
          {user && <ActionButton icon={Lock} label="Đổi mật khẩu" onClick={onChangePassword} />}
          {canEditProfile && <ActionButton icon={UserCog} label="Đổi thông tin Profile" onClick={onEditProfile} />}
          {canCreateTask && <ActionButton icon={Plus} label="Tạo Task" onClick={onCreateTask} variant="primary" />}

          {user ? (
            <div className="header-userbox">
              <span className="header-user-meta">
                {user.display_name}
                <span className="header-user-role">{user.role}</span>
              </span>
              <button className="btn btn-ghost btn-sm" onClick={logout} type="button">
                <LogOut size={14} /> Đăng xuất
              </button>
            </div>
          ) : (
            <ActionButton icon={LogIn} label="Đăng nhập" onClick={onLogin} />
          )}
        </div>
      </header>

      {isMobile && (
        <>
          <header className="header mobile-header mobile-only">
            <div className="header-left">
              <button className="btn-icon" onClick={onToggleSidebar} title={sidebarOpen ? 'Ẩn sidebar' : 'Mở sidebar'} type="button">
                {sidebarOpen ? <X size={18} /> : <PanelLeft size={18} />}
              </button>
              <div className="header-logo compact">
                <img src="/logo.png" alt="Logo" className="header-logo-image" />
                <span>Tien Le's Tasks</span>
              </div>
            </div>
            {user ? (
              <span className="mobile-user-chip">{user.display_name}</span>
            ) : (
              <button className="btn btn-ghost btn-sm" onClick={onLogin} type="button"><LogIn size={14} /> Đăng nhập</button>
            )}
          </header>

          <div className="mobile-dock-wrap mobile-only">
            {menuOpen && (
              <div className="mobile-menu-sheet">
                {menuItems.map((item) => (
                  <button key={item.key} className="mobile-menu-item" onClick={() => handleMenuItemClick(item.onClick)} type="button">
                    <item.icon size={16} />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            )}

            <nav className="mobile-dock">
              <ActionButton icon={Inbox} label="Yêu Cầu" onClick={requestAction} mobile />
              <ActionButton
                icon={Users}
                label="User"
                onClick={canViewUsers ? onManageUsers : onLogin}
                mobile
              />
              <button className="mobile-create-btn" type="button" onClick={canCreateTask ? onCreateTask : onLogin}>
                <Plus size={22} />
              </button>
              <ActionButton
                icon={ScrollText}
                label="Logs"
                onClick={canViewLogs ? onShowLogs : onLogin}
                mobile
              />
              <button className="btn btn-ghost mobile-dock-btn" type="button" onClick={() => setMenuOpen((prev) => !prev)}>
                <Menu size={18} />
                <span className="mobile-dock-label">Menu</span>
              </button>
            </nav>
          </div>
        </>
      )}
    </>
  );
}
